import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { BackIcon, CheckIcon } from '../Brand/Icon';
import { measureLinkLabel, LINK_LABEL_MAX_CHARS, LINK_LABEL_MAX_WORDS } from '../../utils/externalLinkText';
import useViewport from '../../hooks/useViewport';
import './labelDialog.scss';

export default function LinkLabelDialog({ href, label, onSave, onCancel }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(label || '');
  const input = useRef(null);
  const composing = useRef(false);
  const viewport = useViewport();
  const [reservedHeight, setReservedHeight] = useState(0);
  const measured = measureLinkLabel(value);
  const invalid = Boolean(measured.text) && !measured.valid;
  const compact = viewport.height - reservedHeight < 320;
  useLayoutEffect(() => {
    // Use the actual toolbar position: the slider below it disappears while
    // typing, and iOS can pan the visual viewport above the keyboard.
    const controls = document.querySelector('.comment-editor-container .editor-toolbar')
      || document.querySelector('footer.ant-layout-footer');
    const update = () => {
      const bounds = controls?.getBoundingClientRect();
      const height = bounds?.height > 0 && bounds.top >= viewport.offsetTop
        ? Math.max(0, viewport.offsetTop + viewport.height - bounds.top) : 0;
      setReservedHeight(height);
    };
    update();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    if (controls) {
      observer?.observe(controls);
      // A keyboard transition can move controls without resizing the row.
      if (controls.parentElement) observer?.observe(controls.parentElement);
      const editor = controls.closest('.comment-editor-container');
      if (editor) observer?.observe(editor);
    }
    return () => observer?.disconnect();
  }, [viewport.height, viewport.offsetTop, viewport.keyboardOpen]);
  useEffect(() => { input.current?.focus({ preventScroll: true }); }, []);
  const save = event => {
    event.preventDefault();
    if (!composing.current && !invalid) onSave(measured.text || null);
  };
  return <Modal open title={t('brand:linkEditText')} footer={null} closable={false}
    onCancel={onCancel} focusTriggerAfterClose={false} width="auto"
    className={`link-label-dialog${compact ? ' link-label-dialog--compact' : ''}`}
    style={{ '--link-label-controls-height': `${reservedHeight}px` }}>
    <form onSubmit={save}>
      <div className="link-label-dialog__fields">
        <span className="link-label-dialog__destination" dir="auto">{href}</span>
        <label htmlFor="wiw-link-label">{t('brand:linkText')}</label>
        <input ref={input} id="wiw-link-label" value={value} onChange={event => setValue(event.target.value)}
          onCompositionStart={() => { composing.current = true; }}
          onCompositionEnd={() => { composing.current = false; }}
          onKeyDown={event => {
            // Enter first commits an IME candidate, rather than the link label.
            // Safari can report compositionend before this keydown; 229 still
            // identifies the native composition event in that ordering.
            if (event.key === 'Enter' && (composing.current || event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229)) {
              event.preventDefault();
            }
          }}
          aria-invalid={invalid} aria-describedby="wiw-link-label-limit" placeholder={t('brand:linkAutomaticText')} dir="auto" />
        <p id="wiw-link-label-limit" role={invalid ? 'alert' : undefined}>
          {t('brand:linkTextLimit', { words: LINK_LABEL_MAX_WORDS, count: LINK_LABEL_MAX_CHARS })}
        </p>
      </div>
      <div className="link-label-dialog__actions">
        <button type="button" onClick={onCancel} aria-label={t('brand:linkTextCancel')}><BackIcon /></button>
        <button type="submit" disabled={invalid} aria-label={t('brand:linkTextSave')}><CheckIcon /></button>
      </div>
    </form>
  </Modal>;
}
